import { useQuery, useQueryClient } from '@tanstack/react-query';
import { readSessionCookie, clearSessionCookie } from '@/shared/utils/cookies';
import type { Session } from '@/shared/types/session';

/**
 * Single source of truth for "who's signed in right now" across the app.
 *
 * Implementation choice — TanStack Query over Context:
 *   - Sign-in / sign-out are mutations that already use the query cache.
 *     Driving the session through that same cache means `invalidateQueries`
 *     on `sessionKeys.current` (or `setQueryData`) immediately propagates
 *     to every `useSession()` caller, without a separate context broadcast.
 *   - Suspense + error boundary integration comes free.
 *
 * `queryFn` is synchronous (reads `document.cookie`) — TanStack Query treats
 * this as a normal query that resolves immediately. We *don't* persist
 * across remounts via `initialData` because the cookie can change in another
 * tab; reading it every mount is cheap and stays fresh.
 */

export const sessionKeys = {
    all:     ['session'] as const,
    current: () => [...sessionKeys.all, 'current'] as const,
} as const;

export function useSession() {
    const query = useQuery<Session | null>({
        queryKey: sessionKeys.current(),
        queryFn:  () => readSessionCookie(),
        staleTime: Infinity,        // Cookie only changes on sign-in / sign-out, which we drive ourselves.
        gcTime:    Infinity,
    });
    return {
        session:        query.data ?? null,
        isLoading:      query.isLoading,
        isAuthenticated: query.data != null,
    };
}

/** Imperative session access for code outside React (interceptors, etc.). */
export function getSessionSnapshot(): Session | null {
    return readSessionCookie();
}

/** Hook returning a callback that clears the session and resets the cache.
 *  Used by sign-out and by the 401 interceptor's local-cleanup path. */
export function useClearSession(): () => void {
    const qc = useQueryClient();
    return () => {
        clearSessionCookie();
        qc.setQueryData(sessionKeys.current(), null);
        // Drop every cached query — they were tenant-scoped and the next
        // sign-in is potentially a different tenant.
        qc.clear();
    };
}
